import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import useOutsideClick from '../../hooks/useOutsideClick';
import './select-field.scss';

export interface SelectFieldOption {
    /** Submitted value — identical to what the native <option value> used. */
    value: string;
    label: string;
    /** Rendered above the rest, before the separator (e.g. Sweden / USA). */
    pinned?: boolean;
}

export interface SelectFieldProps {
    options: SelectFieldOption[];
    value: string;
    onChange: (value: string) => void;
    placeholder?: string;
    disabled?: boolean;
    /** Adds a filter box — worth it once the list is long (countries). */
    searchable?: boolean;
    /** Decorative icon in the leading edge of the control. */
    leadingIcon?: React.ReactNode;
    /** Marks the control as successfully filled, for theming. */
    valid?: boolean;
    id?: string;
    ariaLabel?: string;
    className?: string;
}

/**
 * Themed single-select. A native <select> renders its list through the OS and
 * cannot be styled, so this is a listbox built from real elements — same value
 * contract as the <select> it replaces: it only reports `value` upward.
 */
export const SelectField: React.FC<SelectFieldProps> = ({
    options,
    value,
    onChange,
    placeholder,
    disabled,
    searchable = false,
    leadingIcon,
    valid,
    id,
    ariaLabel,
    className,
}) => {
    const { t } = useTranslation();
    const [open, setOpen] = useState(false);
    const [query, setQuery] = useState('');
    const [activeIndex, setActiveIndex] = useState(0);
    /* Opens upward when there isn't room below — the country field sits near
       the bottom of the wizard, where a downward menu would run past the card. */
    const [dropUp, setDropUp] = useState(false);

    const buttonRef = useRef<HTMLButtonElement | null>(null);
    const searchRef = useRef<HTMLInputElement | null>(null);
    const listRef = useRef<HTMLUListElement | null>(null);

    const close = useCallback(() => {
        setOpen(false);
        setQuery('');
    }, []);

    const rootRef = useOutsideClick(close) as React.MutableRefObject<HTMLDivElement | null>;

    const baseId = id || 'inv-select';
    const listId = `${baseId}-listbox`;

    const selected = options.find((option) => option.value === value);

    const visible = useMemo(() => {
        const needle = query.trim().toLowerCase();
        const matched = needle
            ? options.filter((option) => option.label.toLowerCase().includes(needle))
            : options;

        // pinned entries stay on top, but only while the user is not filtering
        if (needle) return matched;
        return [...matched].sort((a, b) => Number(!!b.pinned) - Number(!!a.pinned));
    }, [options, query]);

    useLayoutEffect(() => {
        if (!open) return;

        const control = buttonRef.current?.getBoundingClientRect();
        if (!control) return;

        const menuHeight = 300; // search box + list at its tallest
        const below = window.innerHeight - control.bottom;
        const above = control.top;
        setDropUp(below < menuHeight && above > below);
    }, [open]);

    /* keep the highlighted row meaningful: the selected one when opening,
       the first match while filtering */
    useEffect(() => {
        if (!open) return;
        const selectedIndex = visible.findIndex((option) => option.value === value);
        setActiveIndex(query ? 0 : selectedIndex >= 0 ? selectedIndex : 0);
    }, [open, query, value, visible]);

    useEffect(() => {
        if (!open) return;
        if (searchable) searchRef.current?.focus();

        const active = listRef.current?.children[activeIndex] as HTMLElement | undefined;
        active?.scrollIntoView({ block: 'nearest' });
    }, [open, activeIndex, searchable]);

    const commit = (option: SelectFieldOption) => {
        onChange(option.value);
        close();
        buttonRef.current?.focus();
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (disabled) return;

        if (!open) {
            if (e.key === 'ArrowDown' || e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                setOpen(true);
            }
            return;
        }

        switch (e.key) {
            case 'ArrowDown':
                e.preventDefault();
                setActiveIndex((i) => (visible.length ? (i + 1) % visible.length : 0));
                break;
            case 'ArrowUp':
                e.preventDefault();
                setActiveIndex((i) => (visible.length ? (i - 1 + visible.length) % visible.length : 0));
                break;
            case 'Home':
                e.preventDefault();
                setActiveIndex(0);
                break;
            case 'End':
                e.preventDefault();
                setActiveIndex(Math.max(visible.length - 1, 0));
                break;
            case 'Enter':
                e.preventDefault();
                if (visible[activeIndex]) commit(visible[activeIndex]);
                break;
            case 'Escape':
                e.preventDefault();
                close();
                buttonRef.current?.focus();
                break;
            default:
                break;
        }
    };

    return (
        <div
            ref={rootRef}
            onKeyDown={handleKeyDown}
            className={[
                'inv-select',
                open ? 'is-open' : '',
                open && dropUp ? 'is-up' : '',
                valid && !open ? 'is-valid' : '',
                disabled ? 'is-disabled' : '',
                className || '',
            ]
                .filter(Boolean)
                .join(' ')}
        >
            <button
                ref={buttonRef}
                type="button"
                id={baseId}
                disabled={disabled}
                aria-haspopup="listbox"
                aria-expanded={open}
                aria-controls={open ? listId : undefined}
                aria-label={ariaLabel}
                className="inv-select__control"
                onClick={() => setOpen((prev) => !prev)}
            >
                {leadingIcon && (
                    <span className="inv-select__lead" aria-hidden="true">
                        {leadingIcon}
                    </span>
                )}

                <span className={`inv-select__value${selected ? '' : ' is-placeholder'}`}>
                    {selected ? selected.label : placeholder}
                </span>

                <span className="inv-select__chevron" aria-hidden="true">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                        <path d="M6 9.5l6 6 6-6" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                </span>
            </button>

            {open && (
                <div className="inv-select__menu">
                    {searchable && (
                        <div className="inv-select__search">
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                                <circle cx="11" cy="11" r="6.5" stroke="currentColor" strokeWidth="2" />
                                <path d="M16 16l4.5 4.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                            </svg>
                            <input
                                ref={searchRef}
                                type="text"
                                value={query}
                                placeholder={String(t('SEARCH_PLACEHOLDER'))}
                                onChange={(e) => setQuery(e.target.value)}
                                aria-label={String(t('SEARCH_PLACEHOLDER'))}
                            />
                        </div>
                    )}

                    {visible.length === 0 ? (
                        <p className="inv-select__empty">{t('NO_RESULTS_FOUND')}</p>
                    ) : (
                        <ul ref={listRef} id={listId} role="listbox" className="inv-select__list">
                            {visible.map((option, index) => {
                                const isSelected = option.value === value;
                                const showDivider = !query && !option.pinned && index > 0 && !!visible[index - 1]?.pinned;

                                return (
                                    <li
                                        key={`${option.value}-${index}`}
                                        role="option"
                                        aria-selected={isSelected}
                                        className={[
                                            'inv-select__option',
                                            index === activeIndex ? 'is-active' : '',
                                            isSelected ? 'is-selected' : '',
                                            showDivider ? 'has-divider' : '',
                                        ]
                                            .filter(Boolean)
                                            .join(' ')}
                                        onMouseEnter={() => setActiveIndex(index)}
                                        onClick={() => commit(option)}
                                    >
                                        <span>{option.label}</span>
                                        {isSelected && (
                                            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                                                <path d="M5 12.5l4.5 4.5L19 7" stroke="currentColor" strokeWidth="2.8" strokeLinecap="round" strokeLinejoin="round" />
                                            </svg>
                                        )}
                                    </li>
                                );
                            })}
                        </ul>
                    )}
                </div>
            )}
        </div>
    );
};

export default SelectField;
